import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  normalizeMobileFooterId,
  readFooterCache,
  shouldRepaintFooterOnResume,
  subscribeTabResume,
  syncMobileFooterViewportClass,
  writeFooterCache,
} from "../../common/mobileFooter";
import { getMobileFooterComponent } from "./registry";

type MobileFooterProps = {
  onCasino: (fromMobile?: boolean) => void;
};

/**
 * Resolves domain footer id → component immediately via eager registry.
 * On long tab-hide / bfcache restore, remounts locally (no API) so a dropped
 * compositor layer cannot leave the bar invisible until refresh.
 */
const MobileFooter = ({ onCasino }: MobileFooterProps) => {
  const DD = useSelector((e: any) => e.domainDetails);
  const [paintKey, setPaintKey] = useState(0);
  const cached = useMemo(() => readFooterCache(), [paintKey]);
  const hasSite = !!(DD && Object.keys(DD).length);
  const styleId = normalizeMobileFooterId(
    hasSite ? DD.mobileFooter : cached?.mobileFooter
  );
  const schema = hasSite
    ? String(DD.mobileFooterSchema || "")
    : String(cached?.mobileFooterSchema || "");
  const activeColor = hasSite
    ? String(DD.mobileFooterActiveColor || "")
    : String(cached?.mobileFooterActiveColor || "");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, [paintKey]);

  useEffect(() => {
    if (!hasSite) return;
    writeFooterCache(styleId, schema, activeColor);
  }, [hasSite, styleId, schema, activeColor]);

  useEffect(() => {
    syncMobileFooterViewportClass();
    return subscribeTabResume((info) => {
      syncMobileFooterViewportClass();
      if (!shouldRepaintFooterOnResume(info)) return;
      setReady(false);
      setPaintKey((key) => key + 1);
    });
  }, []);

  // Keep the bar on /user/profile, /user/mybet, and every other account route.
  const FooterView = getMobileFooterComponent(styleId);

  return (
    <FooterView
      key={`${styleId}-${paintKey}`}
      onCasino={onCasino}
      schema={schema}
      activeColor={activeColor}
      ready={ready}
    />
  );
};

export default MobileFooter;
